import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  FORMAT_TEXT_COMMAND,
  TextFormatType,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useEffect } from 'react';
import {
  $isWhatsAppBoldNode,
  $isWhatsAppItalicNode,
  $isWhatsAppMonospaceNode,
  $isWhatsAppStrikethroughNode,
} from '../nodes';

/**
 * Plugin to handle Enter key behavior inside inline formatting nodes
 * (bold, italic, strikethrough, monospace)
 * 
 * When Enter is pressed inside an inline formatting node, we want to:
 * 1. Split the formatted node at the cursor position
 * 2. Create a new paragraph with the text after the cursor
 * 3. Preserve the formatting for the text before the cursor
 * 4. Remove the formatting from the text after the cursor (new line should be plain)
 */
export function WhatsAppInlineFormattingPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: KeyboardEvent | null) => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return false;
          }
  
          const anchorNode = selection.anchor.getNode();
          
          // Check if we're inside any inline formatting node
          const isInlineFormatNode = 
            $isWhatsAppBoldNode(anchorNode) ||
            $isWhatsAppItalicNode(anchorNode) ||
            $isWhatsAppStrikethroughNode(anchorNode) ||
            $isWhatsAppMonospaceNode(anchorNode);
  
          if (!isInlineFormatNode || !$isTextNode(anchorNode)) {
            return false;
          }
  
          // Prevent default Enter behavior
          event?.preventDefault();
  
          const offset = selection.anchor.offset;
          const textContent = anchorNode.getTextContent();
  
          // If cursor is at the end, just create a new paragraph
          if (offset === textContent.length) {
            const newParagraph = $createParagraphNode();
            const currentParagraph = anchorNode.getTopLevelElementOrThrow();
            currentParagraph.insertAfter(newParagraph);
            newParagraph.selectStart();
            return true;
          }
  
          // If cursor is at the beginning, create empty paragraph above and convert formatted node to plain text
          if (offset === 0) {
            const newParagraph = $createParagraphNode();
            const currentParagraph = anchorNode.getTopLevelElementOrThrow();
            
            // Replace the formatted node with plain text
            const plainTextNode = $createTextNode(textContent);
            anchorNode.replace(plainTextNode);
            
            // Insert empty paragraph before
            currentParagraph.insertBefore(newParagraph);
            
            // Move cursor to the beginning of the current paragraph (which now has plain text)
            currentParagraph.selectStart();
            return true;
          }
  
          // Split the text node at cursor position
          if ($isTextNode(anchorNode) && anchorNode.splitText) {
            const [textBefore, textAfter] = anchorNode.splitText(offset);
  
            // Create a new paragraph with the text after cursor (unformatted)
            const newParagraph = $createParagraphNode();
            if (textAfter) {
              // Remove textAfter from current paragraph
              textAfter.remove();
              // Add it as plain text to new paragraph
              newParagraph.append($createTextNode(textAfter.getTextContent()));
            }
  
            // Insert the new paragraph after the current paragraph
            const currentParagraph = anchorNode.getTopLevelElementOrThrow();
            currentParagraph.insertAfter(newParagraph);
  
            // Move cursor to the beginning of the new paragraph
            newParagraph.selectStart();
          }
  
          return true; // Stop propagation
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        (formatType: TextFormatType) => {
          // Only handle WhatsApp formats
          if (!['bold', 'italic', 'strikethrough', 'code'].includes(formatType)) {
            return false;
          }
  
          const selection = $getSelection();
          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return false;
          }
  
          // Check if we need to insert spaces around the selection
          // WhatsApp requires bold/italic/etc to be surrounded by spaces/boundaries if inside a word
          const anchor = selection.anchor;
          const focus = selection.focus;
          const [startPoint, endPoint] = anchor.isBefore(focus) ? [anchor, focus] : [focus, anchor];
  
          let modified = false;
  
          // Check After (Do first to avoid offsetting Start if in same node)
          // Actually, Lexical updates points if we modify nodes.
          
          // Check END boundary
          let needsSpaceAfter = false;
          const endNode = endPoint.getNode();
          if ($isTextNode(endNode)) {
             const text = endNode.getTextContent();
             const offset = endPoint.offset;
             // If cursor is NOT at end of node, check char at offset
             if (offset < text.length) {
                 if (!/\s/.test(text[offset])) needsSpaceAfter = true;
             } else {
                 // Cursor at end of node, check next sibling text
                 const nextSibling = endNode.getNextSibling();
                 if ($isTextNode(nextSibling)) {
                     const nextText = nextSibling.getTextContent();
                     if (nextText.length > 0 && !/\s/.test(nextText[0])) {
                         needsSpaceAfter = true;
                     }
                 }
             }
          }
  
          // Check START boundary
          let needsSpaceBefore = false;
          const startNode = startPoint.getNode();
          if ($isTextNode(startNode)) {
              const offset = startPoint.offset;
              if (offset > 0) {
                  const text = startNode.getTextContent();
                  if (!/\s/.test(text[offset - 1])) needsSpaceBefore = true;
              } else {
                  // Offset 0, check prev sibling
                  const prevSibling = startNode.getPreviousSibling();
                  if ($isTextNode(prevSibling)) {
                      const prevText = prevSibling.getTextContent();
                      if (prevText.length > 0 && !/\s/.test(prevText[prevText.length - 1])) {
                          needsSpaceBefore = true;
                      }
                  }
              }
          }
  
          if (needsSpaceAfter || needsSpaceBefore) {
              // Apply spaces
              // We use selection.insertText which is safer but replaces content.
              // To avoid replacing content if it spans multiple nodes, we should be careful.
              // However, the "sub-word" case usually implies a single text node selection.
              
              if (startNode.getKey() === endNode.getKey() && $isTextNode(startNode)) {
                  // Single node selection
                  const textContent = selection.getTextContent();
                  const newText = (needsSpaceBefore ? ' ' : '') + textContent + (needsSpaceAfter ? ' ' : '');
                  selection.insertText(newText);
                  
                  // Now re-select the inner text (excluding space)
                  // insertText puts cursor at end.
                  // We need to calculate where the inner text is.
                  // Current selection is collapsed at the end of inserted text.
                  const newSelection = $getSelection();
                  if ($isRangeSelection(newSelection)) {
                        // Move anchor back by (length + suffix space)
                        // Move focus back by (suffix space)
                        const shiftAnchor = textContent.length + (needsSpaceAfter ? 1 : 0);
                        const shiftFocus = (needsSpaceAfter ? 1 : 0);
                        
                        // We assume simple moves work (no complex unicode handling needed for basic spaces)
                        const anchor = newSelection.anchor;
                        const focus = newSelection.focus;
                        
                        // We can modify the selection points
                        // Actually easier: simply select the range based on offsets if we know the node.
                        // insertText might split nodes if there was formatting? No, pure text insertion.
                        // The anchor/focus point is in the text node.
                        
                        const currentOffset = anchor.offset;
                        const newAnchorOffset = currentOffset - shiftAnchor;
                        const newFocusOffset = currentOffset - shiftFocus;
                        
                        // Flip if needed? insertText collapses to end, so anchor=focus=end.
                        // So we want selection from (End - TotalLen + BeforeSpace) to (End - AfterSpace).
                        
                        // Wait, shiftAnchor = textContent.length + afterSpace. 
                        // Start = End - shiftAnchor.
                        
                        // Correct logic:
                        // inserted: " ccccc " (len 7).
                        // End is at 7.
                        // Start of text: 7 - 1 - 5 = 1.
                        // End of text: 7 - 1 = 6.
                        // Range: 1 to 6.
                        
                        const endOffset = currentOffset - (needsSpaceAfter ? 1 : 0);
                        const startOffset = endOffset - textContent.length;
                        
                        const node = anchor.getNode(); // Should be the text node
                        if ($isTextNode(node)) {
                             // Correctly set selection
                             newSelection.setTextNodeRange(node, startOffset, node, endOffset);
                        }
                  }
                  modified = true;
              } else {
                  // Multi-node selection: insert spaces at boundaries
                  // This is more complex. For now, let's only handle single-node "partial word" case
                  // which is the user's primary complaint ("middle of word").
                  
                  // If we need space at edges of a multi-node selection, we can insert nodes.
                  if (needsSpaceAfter) {
                      // Insert space after endPoint
                      // If endPoint is text, split it?
                      // Easier: just append space to the node if at end.
                      // If partially selected, split.
                      // This is handled by Lexical's splitText logic mostly.
                      
                      // For safety on complex selections, we might skip or try a simpler approach.
                      // Let's attempt insertion.
                      const node = endPoint.getNode();
                       if ($isTextNode(node)) {
                          if (endPoint.offset === node.getTextContentSize()) {
                              node.insertAfter($createTextNode(' '));
                          } else {
                              const [, right] = node.splitText(endPoint.offset);
                              if (right) right.insertBefore($createTextNode(' '));
                          }
                       }
                  }
                  
                  if (needsSpaceBefore) {
                       const node = startPoint.getNode();
                       if ($isTextNode(node)) {
                          if (startPoint.offset === 0) {
                              node.insertBefore($createTextNode(' '));
                          } else {
                              const [left] = node.splitText(startPoint.offset);
                              left.insertAfter($createTextNode(' '));
                          }
                       }
                  }
                  // We don't need to update selection because it usually tracks the split (original node vs new node).
              }
          }
  
          // Return false to let the default formatting command proceed
          // If we modified the text (inserted spaces), the selection should now encompass the target text 
          // (which is what we want to validly bold).
          // Exception: update selection logic above ensured we select the text *inside* the spaces for single node.
          
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}
